import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Wrench, Clock, CheckCircle, AlertCircle } from 'lucide-react';
import { MechanicService } from '../../services/mechanicService';

const MechanicDashboard = () => {
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem('roadserve_current_user') || '{}');

  const { data: requestsData, isLoading: requestsLoading } = useQuery({
    queryKey: ['mechanicRequests', user?.id],
    queryFn: () => MechanicService.getAssignedRequests(user?.id),
    enabled: !!user?.id,
  });

  const { data: performanceData, isLoading: performanceLoading } = useQuery({
    queryKey: ['mechanicPerformance', user?.id],
    queryFn: () => MechanicService.getPerformanceStats(user?.id),
    enabled: !!user?.id,
  });

  const requests = requestsData?.data || [];
  const performance = performanceData?.data || {};

  const stats = {
    activeJobs: requests.filter(r => r.status === 'in_progress' || r.status === 'assigned').length,
    completedToday: requests.filter(r => {
      if (!r.completed_at) return false;
      const today = new Date();
      const completed = new Date(r.completed_at);
      return completed.toDateString() === today.toDateString() && r.status === 'completed';
    }).length,
    rating: performance.rating || performance.averageRating || 0,
    pending: requests.filter(r => r.status === 'pending').length,
  };

  if (requestsLoading || performanceLoading) {
    return (
      <div className="space-y-6">
        <div className="bg-gradient-to-r from-orange-500 to-red-600 rounded-xl p-6 text-white">
          <h1 className="text-3xl font-bold mb-2">Mechanic Dashboard</h1>
          <p className="text-white/80">Manage your work orders</p>
        </div>
        <div className="flex items-center justify-center py-12">
          <div className="w-16 h-16 border-4 border-orange-500 border-t-transparent rounded-full spinner"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-orange-500 to-red-600 rounded-xl p-6 text-white">
        <h1 className="text-3xl font-bold mb-2">Mechanic Dashboard</h1>
        <p className="text-white/80">Manage your work orders</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-1">Active Jobs</p>
              <p className="text-2xl font-bold text-gray-900">{stats.activeJobs}</p>
            </div>
            <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
              <Wrench size={24} className="text-orange-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-1">Completed Today</p>
              <p className="text-2xl font-bold text-gray-900">{stats.completedToday}</p>
            </div>
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
              <CheckCircle size={24} className="text-green-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-1">Rating</p>
              <p className="text-2xl font-bold text-gray-900">{stats.rating.toFixed(1)}</p>
            </div>
            <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center">
              <span className="text-yellow-600 text-xl">⭐</span>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-1">Pending</p>
              <p className="text-2xl font-bold text-gray-900">{stats.pending}</p>
            </div>
            <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center">
              <AlertCircle size={24} className="text-red-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
        <h2 className="text-xl font-bold text-gray-900 mb-4">Quick Actions</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <button 
            onClick={() => navigate('/mechanic/requests')}
            className="px-4 py-3 bg-orange-500 text-white rounded-lg hover:bg-orange-600"
          >
            View All Requests
          </button>
          <button 
            onClick={() => navigate('/mechanic/chat')}
            className="px-4 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
          >
            Open Chat
          </button>
        </div>
      </div>

      {/* Recent Requests */}
      <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
        <h2 className="text-xl font-bold text-gray-900 mb-4">Recent Requests</h2>
        {requests.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <p>No requests assigned yet</p>
          </div>
        ) : (
          <div className="space-y-3">
            {requests.slice(0, 5).map((request) => (
              <div key={request.id} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                <div>
                  <p className="font-semibold text-gray-900">{request.service_type || 'Service Request'}</p>
                  <p className="text-sm text-gray-600">{request.address || 'No address'}</p>
                </div>
                <span className={`px-3 py-1 text-xs font-medium rounded-full ${
                  request.status === 'completed' ? 'bg-green-100 text-green-800' :
                  request.status === 'in_progress' ? 'bg-blue-100 text-blue-800' :
                  'bg-yellow-100 text-yellow-800'
                }`}>
                  {request.status}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default MechanicDashboard;

